import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, MessageCircleIcon, Loader2Icon } from 'lucide-react';
const SERVICE_TYPES = ['Residential Cleaning', 'Commercial Cleaning', 'Airbnb Turnover', 'Deep Cleaning', 'Post-Construction'];
const FREQUENCIES = ['One-time', 'Weekly', 'Bi-Weekly', 'Monthly'];
interface FormState {
  name: string;
  email: string;
  phone: string;
  zipCode: string;
  service: string;
  frequency: string;
  notes: string;
}
const EMPTY: FormState = {
  name: '',
  email: '',
  phone: '',
  zipCode: '',
  service: '',
  frequency: '',
  notes: ''
};
export function QuoteForm() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const update = (key: keyof FormState, value: string) => setForm(f => ({
    ...f,
    [key]: value
  }));
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'submitting' || !e.currentTarget.checkValidity()) return;
    setStatus('submitting');
    window.location.href = createQuoteEmail(form);
    window.setTimeout(() => setStatus('success'), 400);
  };
  const inputClass = 'w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate placeholder:text-slate-400 outline-none transition-colors focus:border-magenta focus:ring-2 focus:ring-magenta/20';
  const labelClass = 'block text-sm font-medium text-slate-soft mb-1.5';
  return <section id="quote" className="w-full bg-slate-50" data-id="element-178" data-mp="8fjd2ofmnwad">
      <div className="mx-auto max-w-3xl px-5 py-20 sm:px-8 sm:py-32" data-id="element-179" data-mp="8dyhcvhku7ax">
        <div className="text-center" data-id="element-180" data-mp="d7jjmzq7dnfx">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-magenta" data-id="element-181" data-mp="i64iovtpy12s">
            Get Started
          </span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate" data-id="element-182" data-mp="m8heosinykmd">
            Get a Custom Quote
          </h2>
          <p className="mt-4 text-slate-soft" data-id="element-183" data-mp="r4g6yr744ms2">
            Tell us about your space and we&apos;ll craft a plan just for you.
          </p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.5
      }} className="mt-10 rounded-3xl bg-white p-6 sm:mt-12 sm:p-10 shadow-[0_8px_40px_rgba(15,23,42,0.06)] ring-1 ring-slate-100" data-id="element-184" data-mp="2j9dex48djnn7">
          {status === 'success' ? <div className="flex flex-col items-center py-12 text-center" data-id="element-185" data-mp="3fxbg0pv2cn2y">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-magenta-soft text-magenta" data-id="element-186" data-mp="3az9u7x6969ns">
                <CheckCircle2Icon className="h-9 w-9" data-id="element-187" data-mp="1lld3adxjp92b" />
              </span>
              <h3 className="mt-5 font-display text-2xl font-bold text-slate" data-id="element-188" data-mp="1gma9jpn0mp9n">
                Thank you!
              </h3>
              <p className="mt-2 max-w-sm text-slate-soft" data-id="element-189" data-mp="3sa5bpdvsvrbd">
                Ally will text you shortly with your personalized quote.
              </p>
            </div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-190" data-mp="3jfh8dt9scjp7">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-191" data-mp="3ni41dxi70anz">
                <div data-id="element-192" data-mp="3ng14ctimhh9r">
                  <label htmlFor="name" className={labelClass} data-id="element-193" data-mp="39icknp0akg6a">
                    Name
                  </label>
                  <input id="name" type="text" required value={form.name} onChange={e => update('name', e.target.value)} placeholder="Jane Doe" className={inputClass} data-id="element-194" data-mp="3ehywbu0h1ocz" />
                </div>
                <div data-id="element-195" data-mp="yr2xbg2j25nm">
                  <label htmlFor="email" className={labelClass} data-id="element-196" data-mp="yogs3n34028k">
                    Email
                  </label>
                  <input id="email" type="email" required value={form.email} onChange={e => update('email', e.target.value)} placeholder="jane@email.com" className={inputClass} data-id="element-197" data-mp="e0z1n5pn2smd" />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-198" data-mp="3vbqbutuxzkvt">
                <div data-id="element-199" data-mp="3r9btsbz3kp5l">
                  <label htmlFor="phone" className={labelClass} data-id="element-200" data-mp="3r9lm467m78eg">
                    Phone
                  </label>
                  <input id="phone" type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="(929) 650-9940" className={inputClass} data-id="element-201" data-mp="3hhz5fucich4b" />
                </div>
                <div data-id="element-202" data-mp="33hy4yg05nbpd">
                  <label htmlFor="zipCode" className={labelClass} data-id="element-203" data-mp="33ipzdni0n3e2">
                    ZIP Code <span aria-hidden="true" data-id="element-204" data-mp="1iz40cwpswf4w">*</span>
                  </label>
                  <input id="zipCode" type="text" inputMode="numeric" autoComplete="postal-code" pattern="[0-9]{5}(-[0-9]{4})?" required aria-required="true" value={form.zipCode} onChange={e => update('zipCode', e.target.value)} placeholder="10001" className={inputClass} data-id="element-205" data-mp="1n1h8tsvxmr5d" />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-206" data-mp="1991b97lsvlbu">
                <div data-id="element-207" data-mp="1e243nceucoqg">
                  <label htmlFor="service" className={labelClass} data-id="element-208" data-mp="1e2dludtn9rll">
                    Service Type
                  </label>
                  <select id="service" value={form.service} onChange={e => update('service', e.target.value)} className={inputClass} data-id="element-209" data-mp="gju9lzr1ufmd">
                    <option value="" data-id="element-210" data-mp="9j9yrams6ew8">Select</option>
                    {SERVICE_TYPES.map(s => <option key={s} value={s} data-id="element-211" data-mp="na544cy2mdv2">
                        {s}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-212" data-mp="3qqc2c3xpviwg">
                  <label htmlFor="frequency" className={labelClass} data-id="element-213" data-mp="3qrn829aq30q7">
                    Frequency
                  </label>
                  <select id="frequency" value={form.frequency} onChange={e => update('frequency', e.target.value)} className={inputClass} data-id="element-214" data-mp="3mql79o15qgm4">
                    <option value="" data-id="element-215" data-mp="1fykgsstqck38">Select</option>
                    {FREQUENCIES.map(f => <option key={f} value={f} data-id="element-216" data-mp="1azpxlpruotil">
                        {f}
                      </option>)}
                  </select>
                </div>
              </div>

              <div data-id="element-217" data-mp="2xw5tmt01bq3d">
                <label htmlFor="notes" className={labelClass} data-id="element-218" data-mp="2xwfoatw0ylfe">
                  Notes
                </label>
                <textarea id="notes" rows={4} value={form.notes} onChange={e => update('notes', e.target.value)} placeholder="Include Square Footage, ZIP Code, and number of bedrooms/bathrooms" className={`${inputClass} resize-none`} data-id="element-219" data-mp="2jxg0fogc6aka" />
              </div>

              <button type="submit" disabled={status === 'submitting'} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-magenta px-8 py-4 text-base font-semibold text-white shadow-lg shadow-magenta/25 transition-all hover:bg-magenta-dark disabled:opacity-70" data-id="element-220" data-mp="32vsbxgupzwj1">
                {status === 'submitting' ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-221" data-mp="2096mh0p4ldjb" />
                    Sending…
                  </> : 'Request My Quote'}
              </button>
            </form>}
        </motion.div>

        <div className="mt-8 text-center" data-id="element-222" data-mp="1ljsbxp9tcq2c">
          <a href="sms:+19296509940" className="inline-flex items-center gap-2 text-base font-semibold text-magenta hover:text-magenta-dark transition-colors" data-id="element-223" data-mp="1ll3e5knm1prc">
            <MessageCircleIcon className="h-5 w-5" data-id="element-224" data-mp="2e9halvz1we0s" />
            Prefer to text? Click here to SMS us.
          </a>
        </div>
      </div>
    </section>;
}
function createQuoteEmail(form: FormState) {
  const subject = 'New Ally Cleaning Boutique quote request';
  const body = ['New custom quote request', '', `Name: ${form.name}`, `Email: ${form.email}`, `Phone: ${form.phone || 'Not provided'}`, `ZIP Code: ${form.zipCode}`, `Service Type: ${form.service || 'Not selected'}`, `Frequency: ${form.frequency || 'Not selected'}`, `Notes: ${form.notes || 'None'}`].join('\n');
  return `mailto:allycleaningservices1@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}